import { FC, useEffect, useState } from "react";
import { Toast } from "../../components/atom/Toast/Toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { AccountItemSchema } from "./data/AccountItemSchema";
import { FieldValues, useForm } from "react-hook-form";
import { Form } from "react-router-dom";
import SubmitItem from "../../components/template/SubmitItem/SubmitItem";
import AccountSubmitForm from "./controller/AccountSubmitForm";
import { handleSubmitItem } from "../../helpers/handleSubmitItem";
import { EDIT_USER_URL, RESET_PASSWORD_ANONYMUS } from "../../utils/endpoints";
import { handleResetPassword } from "../../helpers/handleResetPassword";
import api from "../../utils/api";
import { AccountData } from "./data/AccountData";
import { useAtom } from "jotai";
import { authAtom } from "../../provider/authAtom";
import { AlertDialogConfig } from "../../types/Components/Alert/AlertDialogConfig";
import { useAuth } from "../../provider/AuthProvider";

const EditAccount: FC = () => {
  const { addToast } = Toast();
  const [, setIsAlertOpen] = useState(false);
  const [userData, setUserData] = useState<AccountData | undefined>(undefined);
  const [, setAuth] = useAtom(authAtom);
  const [isLoaded, setIsLoaded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { auth } = useAuth();
  const user = auth.user;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(AccountItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  useEffect(() => {
    api.get("/api/User/getselfdata").then((response) => {
      const data = response.data;
      setUserData(data);
      setValue("name", data.name);
      setValue("position", data.position ? data.position : "");
      setValue("email", data.email);
      setValue("phone", data.phone ? data.phone : "");
      setValue("role", data.roles[0].name);
      setValue(
        "province",
        data.userProvince ? data.userProvince.name : undefined,
      );
      setValue("county", data.userCounty ? data.userCounty.name : undefined);
      setValue(
        "organization",
        data.organization ? data.organization.name : undefined,
      );
      setValue("partner", data.partner ? data.partner.name : undefined);
    });
  }, [setValue]);

  const onSubmit = async (data: FieldValues) => {
    const bodyRequest = {
      id: userData?.id,
      name: data.name,
      email: data.email ? data.email : null,
      roles: userData?.roles ? [userData.roles[0].id] : null,
      provinceId: userData?.userProvince ? userData.userProvince.id : null,
      countyId: userData?.userCounty ? userData.userCounty.id : null,
    };

    const isSuccess = await handleSubmitItem(
      () => api.put(EDIT_USER_URL, bodyRequest),
      addToast,
      setIsLoaded,
    );

    if (isSuccess && userData) {
      setAuth((prevAuth) => ({
        ...prevAuth,
        user: {
          ...prevAuth.user,
          name: data.name || "",
          id: data.id || "",
          email: data.email || "",
        },
      }));
    }
  };

  const handlePasswordReset = async () => {
    userData &&
      (await handleResetPassword(
        () => api.post(RESET_PASSWORD_ANONYMUS, { email: userData.email }),
        addToast,
      ));
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SubmitItem
        title="Minha conta"
        handleSubmitItem={handleSubmit(onSubmit)}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        // lastUpdateDate="10/10/2024, 10:10"
      >
        <AccountSubmitForm
          errors={errors}
          control={control}
          handlePasswordReset={
            user.permissions.includes("ResetPassword") && handlePasswordReset
          }
          selectedRole={userData && userData.roles[0]}
          isDisabled={!isEditing}
          isSystemDisabled={true}
        />
      </SubmitItem>
    </Form>
  );
};

export default EditAccount;
